import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Package, Euro, Mail, LogOut, FileText, ChevronRight, X, MessageSquare, CalendarClock, Send, Video, Phone, CheckCircle2, Upload, Loader2, Download, BellRing, UserCircle, FileSignature, CreditCard, History, Search, RefreshCw, Trash2, BarChart3, Eye, Globe, Smartphone, Monitor, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { LOGO, CURRENCY } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { SydneyIntegrationPanel } from "@/components/admin/SydneyIntegrationPanel";

const TRASH_TYPE_FR = { payment: "Paiement", booking: "Dossier", appointment: "Rendez-vous",
  contract: "Contrat", invoice: "Facture", message: "Message", client: "Client", staff: "Employé" };

const TrashBtn = ({ onClick, testid, className = "" }) => (  <button
    type="button"
    data-testid={testid}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    title="Supprimer (déplacer dans la corbeille)"
    className={`shrink-0 p-2 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors ${className}`}
  >
    <Trash2 size={16} />
  </button>
);

export default function AdminDashboard() {  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState([]);
  const [note, setNote] = useState("");
  const [threads, setThreads] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [threadMsgs, setThreadMsgs] = useState([]);
  const [reply, setReply] = useState("");
  const [apptEdit, setApptEdit] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "" });
  const [launchList, setLaunchList] = useState([]);
  const [preregs, setPreregs] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientDetail, setClientDetail] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState(null);
  const [activity, setActivity] = useState([]);
  const [bookingQ, setBookingQ] = useState("");
  const [payFilter, setPayFilter] = useState("all");
  const [clientQ, setClientQ] = useState("");
  const [apptFilter, setApptFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [trash, setTrash] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [advisors, setAdvisors] = useState([]);
  const [advForm, setAdvForm] = useState({ name: "", role: "", description: "" });
  const [advEditId, setAdvEditId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [revForm, setRevForm] = useState({ name: "", rating: 5, text: "", source: "google", date_label: "" });
  const [gCfg, setGCfg] = useState({ rating: 0, review_count: 0, profile_url: "", write_review_url: "", enabled: true, api_configured: false });

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/threads").then((r) => setThreads(r.data)).catch(() => {});
    api.get("/admin/appointments").then((r) => setAppointments(r.data)).catch(() => {});
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/admin/contracts").then((r) => setContracts(r.data)).catch(() => {});
    api.get("/admin/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/admin/advisors").then((r) => setAdvisors(r.data)).catch(() => {});
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/admin/google-reviews-config").then((r) => setGCfg(r.data)).catch(() => {});
    api.get("/admin/preregistrations").then((r) => setPreregs(r.data)).catch(() => {});
    if (user?.role === "admin") {
      api.get("/admin/staff").then((r) => setStaffList(r.data)).catch(() => {});
      api.get("/admin/launch-notifications").then((r) => setLaunchList(r.data)).catch(() => {});
      api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
      api.get("/admin/activity").then((r) => setActivity(r.data)).catch(() => {});
      api.get("/admin/trash").then((r) => setTrash(r.data)).catch(() => {});
      api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    }
    setLastSync(new Date());
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);
  // Synchronisation temps réel : rafraîchissement automatique toutes les 20s.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const id = setInterval(() => loadAll(), 20000); return () => clearInterval(id); }, []);

  const manualRefresh = useCallback(() => {
    setRefreshing(true);
    loadAll();
    setTimeout(() => setRefreshing(false), 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fBookings = useMemo(() => bookings.filter((b) => {
    const okQ = !bookingQ || `${b.pack_name} ${b.user_name} ${b.user_email}`.toLowerCase().includes(bookingQ.toLowerCase());
    const okPay = payFilter === "all" || (payFilter === "paid" ? b.payment_status === "paid" : b.payment_status !== "paid");
    return okQ && okPay;
  }), [bookings, bookingQ, payFilter]);
  const fClients = useMemo(() => clients.filter((c) => !clientQ || `${c.name || ""} ${c.email || ""}`.toLowerCase().includes(clientQ.toLowerCase())), [clients, clientQ]);
  const fAppts = useMemo(() => appointments.filter((a) => apptFilter === "all" || (apptFilter === "pending" ? !["confirmed", "done", "cancelled"].includes(a.status) : a.status === apptFilter)), [appointments, apptFilter]);
  const pendingPay = useMemo(() => bookings.filter((b) => b.payment_status !== "paid").length, [bookings]);
  const pendingAppt = useMemo(() => appointments.filter((a) => !["confirmed", "done", "cancelled"].includes(a.status)).length, [appointments]);
  const unreadMsgs = useMemo(() => threads.reduce((s, t) => s + (t.unread || 0), 0), [threads]);

  const openClient = async (c) => {
    setClientDetail({ loading: true, id: c.id });
    try { const { data } = await api.get(`/admin/clients/${c.id}`); setClientDetail(data); }
    catch { toast.error("Erreur de chargement de la fiche client."); setClientDetail(null); }
  };
  const deleteClient = async (c) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet élément ?")) return;
    try {
      await api.delete(`/admin/items/client/${c.id}`);
      toast.success("Client déplacé dans la corbeille.");
      setClientDetail(null);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de la suppression."); }
  };
  const softDelete = async (entityType, id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet élément ?")) return;
    try {
      await api.delete(`/admin/items/${entityType}/${id}`);
      toast.success("Élément déplacé dans la corbeille.");
      if (selected?.id === id) setSelected(null);
      if (activeThread?.id === id) { setActiveThread(null); }
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de la suppression."); }
  };
  const restoreTrash = async (id) => {
    try { await api.post(`/admin/trash/${id}/restore`); toast.success("Élément restauré."); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de la restauration."); }
  };
  const purgeTrash = async (id) => {
    if (!window.confirm("Supprimer DÉFINITIVEMENT cet élément ? Cette action est irréversible.")) return;
    try { await api.delete(`/admin/trash/${id}`); toast.success("Élément supprimé définitivement."); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur."); }
  };
  const downloadPdf = (type, id) => window.open(`${API}/${type}/${id}/pdf?auth=${localStorage.getItem("wte_token")}`, "_blank");

  const exportPreregCsv = () => {
    const rows = [["Nom", "Email", "Téléphone", "Destination", "Message", "Date"],
      ...preregs.map((p) => [p.name, p.email, p.phone || "", p.destination, p.message || "", new Date(p.created_at).toLocaleString("fr-FR")])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `preinscriptions-canada-2027-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deletePrereg = async (p) => {
    if (!window.confirm(`Supprimer la préinscription de « ${p.email} » ?`)) return;
    try {
      await api.delete(`/admin/preregistrations/${p.id}`);
      toast.success("Préinscription supprimée.");
      const { data } = await api.get("/admin/preregistrations"); setPreregs(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de la suppression."); }
  };

  const exportLaunchCsv = () => {
    const rows = [["Email", "Date d'inscription"], ...launchList.map((l) => [l.email, new Date(l.created_at).toLocaleString("fr-FR")])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `liste-attente-ouverture-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportClientsCsv = async () => {
    try {
      const res = await api.get("/admin/clients/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `clients-worktravelexpert-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'export CSV.");
    }
  };

  const createStaff = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/staff", newStaff);
      toast.success("Compte employé créé.");
      setNewStaff({ name: "", email: "", password: "" });
      const { data } = await api.get("/admin/staff"); setStaffList(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de la création."); }
  };
  const submitAdvisor = async (e) => {
    e.preventDefault();
    if (!advForm.name.trim() || !advForm.role.trim()) { toast.error("Nom et rôle requis."); return; }
    try {
      if (advEditId) {
        await api.put(`/admin/advisors/${advEditId}`, { ...advForm, active: true });
        toast.success("Conseiller mis à jour.");
      } else {
        await api.post("/admin/advisors", { ...advForm, active: true });
        toast.success("Conseiller ajouté.");
      }
      setAdvForm({ name: "", role: "", description: "" });
      setAdvEditId(null);
      const { data } = await api.get("/admin/advisors"); setAdvisors(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement."); }
  };
  const editAdvisor = (a) => { setAdvEditId(a.id); setAdvForm({ name: a.name, role: a.role, description: a.description || "" }); };
  const cancelAdvisorEdit = () => { setAdvEditId(null); setAdvForm({ name: "", role: "", description: "" }); };
  const deleteAdvisor = async (a) => {
    if (!window.confirm(`Supprimer le conseiller « ${a.name} » ?`)) return;
    try {
      await api.delete(`/admin/advisors/${a.id}`);
      toast.success("Conseiller supprimé.");
      if (advEditId === a.id) cancelAdvisorEdit();
      const { data } = await api.get("/admin/advisors"); setAdvisors(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de la suppression."); }
  };
  const addReview = async (e) => {
    e.preventDefault();
    if (!revForm.name.trim() || !revForm.text.trim()) { toast.error("Nom et avis requis."); return; }
    try {
      await api.post("/admin/reviews", { ...revForm, visible: true });
      toast.success("Avis ajouté.");
      setRevForm({ name: "", rating: 5, text: "", source: "google", date_label: "" });
      const { data } = await api.get("/admin/reviews"); setReviews(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'ajout."); }
  };
  const saveGoogleConfig = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/admin/google-reviews-config", {
        rating: Number(gCfg.rating) || 0,
        review_count: Number(gCfg.review_count) || 0,
        profile_url: gCfg.profile_url || "",
        write_review_url: gCfg.write_review_url || "",
        enabled: !!gCfg.enabled,
      });
      setGCfg({ ...data, api_configured: gCfg.api_configured });
      toast.success("Configuration des avis Google enregistrée.");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement."); }
  };
  const toggleReview = async (r) => {
    try {
      await api.patch(`/admin/reviews/${r.id}/visibility`);
      const { data } = await api.get("/admin/reviews"); setReviews(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur."); }
  };
  const deleteReview = async (r) => {
    if (!window.confirm(`Supprimer l'avis de « ${r.name} » ?`)) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Avis supprimé.");
      const { data } = await api.get("/admin/reviews"); setReviews(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de la suppression."); }
  };

  const openThread = async (t) => {
    setActiveThread(t);
    try { const { data } = await api.get(`/admin/messages/${t.user_id}`); setThreadMsgs(data); }
    catch (e) { console.error("Load thread failed", e); }
    loadAll();
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      const { data } = await api.post(`/admin/messages/${activeThread.user_id}`, { text: reply.trim() });
      setThreadMsgs((m) => [...m, data]); setReply("");
    } catch { toast.error("Erreur d'envoi."); }
  };
  const confirmAppt = async () => {
    try {
      await api.patch(`/admin/appointments/${apptEdit.id}`, {
        status: "confirmed", confirmed_datetime: apptEdit.confirmed_datetime || apptEdit.preferred_date,
        meeting_link: apptEdit.meeting_link || null });
      toast.success("Rendez-vous confirmé. Le client a été notifié.");
      setApptEdit(null); loadAll();
    } catch { toast.error("Erreur."); }
  };

  const setApptStatus = async (a, status) => {
    try {
      await api.patch(`/admin/appointments/${a.id}`, { status });
      toast.success("Statut mis à jour. Le client a été notifié.");
      loadAll();
    } catch { toast.error("Erreur."); }
  };

  const openBooking = async (b) => {
    setSelected(b); setNote(b.admin_note || ""); setDocs([]);
    try { const { data } = await api.get(`/admin/bookings/${b.id}/documents`); setDocs(data); } catch {}
  };

  const uploadClientDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setUploadingDoc(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", "Document de l'agence");
    try {
      await api.post(`/admin/clients/${selected.user_id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document envoyé au client. Il a été notifié.");
      const { data } = await api.get(`/admin/bookings/${selected.id}/documents`); setDocs(data);
    } catch { toast.error("Erreur lors de l'envoi du document."); }
    finally { setUploadingDoc(false); e.target.value = ""; }
  };

  const updateStep = async (b, stepIndex) => {
    try {
      await api.patch(`/admin/bookings/${b.id}`, { step_index: parseInt(stepIndex) });
      toast.success("Statut mis à jour. Le client a été notifié.");
      loadAll();
      setSelected({ ...b, step_index: parseInt(stepIndex) });
    } catch { toast.error("Erreur lors de la mise à jour."); }
  };

  const saveNote = async () => {
    try { await api.patch(`/admin/bookings/${selected.id}`, { admin_note: note }); toast.success("Note enregistrée."); loadAll(); }
    catch { toast.error("Erreur."); }
  };

  const download = (id) => window.open(`${API}/documents/${id}/download?auth=${localStorage.getItem("wte_token")}`, "_blank");

  const statCards = stats ? [
    { label: "Clients", value: stats.total_users, icon: Users },
    { label: "Réservations", value: stats.total_bookings, icon: Package },
    { label: "Dossiers payés", value: stats.paid_bookings, icon: Package },
    ...(stats.can_view_revenue ? [{ label: "Revenu", value: CURRENCY(stats.revenue), icon: Euro }] : []),
    { label: "Messages", value: stats.contacts, icon: Mail },
  ] : [];
  const isAdmin = user?.role === "admin";

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F3F4F6]">
      <Seo title="Administration | Work & Travel Expert" description="Tableau de bord administrateur Work & Travel Expert." path="/admin" noindex />
      <header className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-full h-10 w-10 flex items-center justify-center p-0.5"><img src={LOGO} alt="Logo" className="h-full w-full object-contain rounded-full" /></div>
            <span className="font-heading text-xl">{isAdmin ? "Administration" : "Espace Employé"}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/70 hidden sm:block">{user?.email}</span>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-2 text-sm hover:text-gold transition-colors"><LogOut size={18} /> Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="font-heading text-3xl text-navy">{isAdmin ? "Tableau de bord administrateur" : "Tableau de bord employé"}</h1>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="sync-indicator">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span></span>
              Temps réel{lastSync ? ` · ${lastSync.toLocaleTimeString("fr-FR")}` : ""}
            </span>
            <button data-testid="refresh-btn" onClick={manualRefresh} className="flex items-center gap-2 text-sm border border-border bg-white hover:bg-[#F8F9FA] text-navy px-3 py-2 transition-colors">
              <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> Rafraîchir
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white border border-border p-5">
              <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground">{s.label}</span><s.icon size={18} className="text-gold" /></div>
              <div className="font-heading text-2xl text-navy mt-2">{s.value}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="bookings" className="mt-10">
          <TabsList className="rounded-none bg-white border border-border flex-wrap h-auto">
            {isAdmin && <TabsTrigger value="stats" data-testid="tab-stats" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Statistiques</TabsTrigger>}
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Dossiers{pendingPay > 0 && <span className="ml-2 inline-flex items-center bg-amber-500 text-white text-[10px] rounded-full px-2 py-0.5 leading-none" data-testid="badge-pending-pay">{pendingPay}</span>}</TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Clients</TabsTrigger>
            <TabsTrigger value="threads" data-testid="tab-threads" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Messagerie{unreadMsgs > 0 && <span className="ml-2 inline-flex items-center bg-gold text-white text-[10px] rounded-full px-2 py-0.5 leading-none" data-testid="badge-unread">{unreadMsgs}</span>}</TabsTrigger>
            <TabsTrigger value="rendezvous" data-testid="tab-rendezvous" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Rendez-vous{pendingAppt > 0 && <span className="ml-2 inline-flex items-center bg-amber-500 text-white text-[10px] rounded-full px-2 py-0.5 leading-none" data-testid="badge-pending-appt">{pendingAppt}</span>}</TabsTrigger>
            <TabsTrigger value="contrats" data-testid="tab-contrats" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Contrats &amp; Factures</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Contact</TabsTrigger>
            <TabsTrigger value="conseillers" data-testid="tab-conseillers" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Conseillers</TabsTrigger>
            <TabsTrigger value="avis" data-testid="tab-avis" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Avis</TabsTrigger>
            <TabsTrigger value="preinscriptions" data-testid="tab-preinscriptions" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Préinscriptions{preregs.length > 0 && <span className="ml-2 inline-flex items-center bg-gold text-white text-[10px] rounded-full px-2 py-0.5 leading-none" data-testid="badge-preregs">{preregs.length}</span>}</TabsTrigger>
            {isAdmin && <TabsTrigger value="paiements" data-testid="tab-paiements" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Paiements</TabsTrigger>}
            {isAdmin && <TabsTrigger value="staff" data-testid="tab-staff" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Personnel</TabsTrigger>}
            {isAdmin && <TabsTrigger value="journal" data-testid="tab-journal" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Journal</TabsTrigger>}
            {isAdmin && <TabsTrigger value="waitlist" data-testid="tab-waitlist" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Liste d'attente ouverture</TabsTrigger>}
            {isAdmin && <TabsTrigger value="sydney" data-testid="tab-sydney" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Intégration Sydney</TabsTrigger>}
            {isAdmin && <TabsTrigger value="corbeille" data-testid="tab-corbeille" className="rounded-none data-[state=active]:bg-navy data-[state=active]:text-white">Corbeille{trash.length > 0 && <span className="ml-2 inline-flex items-center bg-red-500 text-white text-[10px] rounded-full px-2 py-0.5 leading-none" data-testid="badge-trash">{trash.length}</span>}</TabsTrigger>}
          </TabsList>

          {isAdmin && (
          <TabsContent value="stats" className="mt-5" data-testid="stats-content">
            {!analytics ? (
              <div className="bg-white border border-border p-10 text-center text-muted-foreground text-sm">Chargement des statistiques…</div>
            ) : (
              <div className="space-y-6">
                {/* Cartes clés */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { icon: Eye, label: "Visiteurs (total)", value: analytics.total_visitors, testid: "stat-total-visitors" },
                    { icon: BarChart3, label: "Visites aujourd'hui", value: analytics.visits_today, testid: "stat-visits-today" },
                    { icon: CalendarClock, label: "Visites (7 jours)", value: analytics.visits_week, testid: "stat-visits-week" },
                    { icon: CalendarClock, label: "Visites (30 jours)", value: analytics.visits_month, testid: "stat-visits-month" },
                    { icon: UserPlus, label: "Inscriptions", value: analytics.registrations, testid: "stat-registrations" },
                    { icon: CreditCard, label: "Paiements", value: analytics.payments, testid: "stat-payments" },
                    { icon: Smartphone, label: "Mobile", value: analytics.by_device.mobile, testid: "stat-mobile" },
                    { icon: Monitor, label: "Ordinateur", value: analytics.by_device.desktop, testid: "stat-desktop" },
                  ].map((s) => (
                    <div key={s.label} data-testid={s.testid} className="bg-white border border-border p-5">
                      <s.icon size={18} className="text-gold" />
                      <p className="text-2xl font-heading text-navy mt-3">{s.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Graphique d'évolution */}
                <div className="bg-white border border-border p-5">
                  <h3 className="font-heading text-lg text-navy mb-4 flex items-center gap-2"><BarChart3 size={18} className="text-gold" /> Évolution (14 derniers jours)</h3>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={analytics.visits_series.map((v, i) => ({ date: v.date, Visites: v.count, Inscriptions: analytics.registrations_series[i]?.count || 0, Paiements: analytics.payments_series[i]?.count || 0 }))}>
                        <defs>
                          <linearGradient id="gVisits" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#C5A059" stopOpacity={0.5} /><stop offset="95%" stopColor="#C5A059" stopOpacity={0} /></linearGradient>
                          <linearGradient id="gReg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0A192F" stopOpacity={0.4} /><stop offset="95%" stopColor="#0A192F" stopOpacity={0} /></linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Area type="monotone" dataKey="Visites" stroke="#C5A059" strokeWidth={2} fill="url(#gVisits)" />
                        <Area type="monotone" dataKey="Inscriptions" stroke="#0A192F" strokeWidth={2} fill="url(#gReg)" />
                        <Area type="monotone" dataKey="Paiements" stroke="#16a34a" strokeWidth={2} fillOpacity={0} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Pays */}
                  <div className="bg-white border border-border p-5">
                    <h3 className="font-heading text-lg text-navy mb-4 flex items-center gap-2"><Globe size={18} className="text-gold" /> Pays des visiteurs</h3>
                    {analytics.by_country.length === 0 && <p className="text-sm text-muted-foreground">Aucune donnée pour l'instant.</p>}
                    <div className="space-y-3">
                      {analytics.by_country.map((c) => {
                        const max = Math.max(...analytics.by_country.map((x) => x.count), 1);
                        return (
                          <div key={c.country} data-testid={`country-${c.country}`}>
                            <div className="flex justify-between text-sm text-navy mb-1"><span>{c.country}</span><span className="text-muted-foreground">{c.count}</span></div>
                            <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden"><div className="h-full bg-gold rounded-full" style={{ width: `${(c.count / max) * 100}%` }} /></div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* Appareils */}
                  <div className="bg-white border border-border p-5">
                    <h3 className="font-heading text-lg text-navy mb-4 flex items-center gap-2"><Smartphone size={18} className="text-gold" /> Appareils</h3>
                    <div className="space-y-3">
                      {[{ k: "mobile", l: "Mobile", icon: Smartphone }, { k: "desktop", l: "Ordinateur", icon: Monitor }, { k: "tablet", l: "Tablette", icon: Smartphone }].map((d) => {
                        const total = (analytics.by_device.mobile + analytics.by_device.desktop + analytics.by_device.tablet) || 1;
                        const val = analytics.by_device[d.k] || 0;
                        return (
                          <div key={d.k}>
                            <div className="flex justify-between text-sm text-navy mb-1"><span className="flex items-center gap-2"><d.icon size={14} className="text-gold" /> {d.l}</span><span className="text-muted-foreground">{val} ({Math.round((val / total) * 100)}%)</span></div>
                            <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden"><div className="h-full bg-navy rounded-full" style={{ width: `${(val / total) * 100}%` }} /></div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-4">Le pays est estimé d'après la langue du navigateur. Pour des statistiques ultra-détaillées (provenance, temps réel), consultez Google Analytics (balise installée).</p>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
          )}

          <TabsContent value="bookings" className="mt-5">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input data-testid="bookings-search" value={bookingQ} onChange={(e) => setBookingQ(e.target.value)} placeholder="Rechercher un dossier (client, email, pack)…" className="w-full bg-white border border-border pl-10 pr-3 py-2.5 text-sm outline-none focus:border-gold transition-colors" />
              </div>
              <Select value={payFilter} onValueChange={setPayFilter}>
                <SelectTrigger data-testid="bookings-filter" className="w-full sm:w-52 rounded-none bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les paiements</SelectItem>
                  <SelectItem value="paid">Payés</SelectItem>
                  <SelectItem value="pending">Non payés</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-white border border-border divide-y divide-border">
              {fBookings.length === 0 && <p className="p-6 text-muted-foreground text-sm">Aucun dossier.</p>}
              {fBookings.map((b) => (
                <div key={b.id} className="w-full flex items-center gap-2 p-4 hover:bg-[#F8F9FA] transition-colors">
                  <button data-testid={`admin-booking-${b.id}`} onClick={() => openBooking(b)} className="flex-1 flex items-center justify-between gap-3 text-left min-w-0">
                    <div className="min-w-0">
                      <p className="text-navy font-medium truncate">{b.pack_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{b.user_name} · {b.user_email}</p>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                      <span className={`text-xs px-2 py-1 whitespace-nowrap hidden sm:inline ${b.payment_status === "paid" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>{b.payment_status === "paid" ? b.steps?.[b.step_index] : "Non payé"}</span>
                      <span className="text-sm text-navy font-medium whitespace-nowrap">{CURRENCY(b.amount)}</span>
                      <ChevronRight size={16} className="text-muted-foreground" />
                    </div>
                  </button>
                  {isAdmin && <TrashBtn testid={`trash-booking-${b.id}`} onClick={() => softDelete("booking", b.id)} />}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="clients" className="mt-5" data-testid="clients-content">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input data-testid="clients-search" value={clientQ} onChange={(e) => setClientQ(e.target.value)} placeholder="Rechercher un client (nom, email)…" className="w-full bg-white border border-border pl-10 pr-3 py-2.5 text-sm outline-none focus:border-gold transition-colors" />
              </div>
              {isAdmin && (
                <button data-testid="clients-export-btn" onClick={exportClientsCsv} className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-white px-4 py-2.5 text-sm transition-colors whitespace-nowrap">
                  <Download size={16} /> Exporter en CSV
                </button>
              )}
            </div>
            <div className="bg-white border border-border divide-y divide-border">
              {fClients.length === 0 && <p className="p-6 text-muted-foreground text-sm">Aucun client.</p>}
              {fClients.map((c) => (
                <div key={c.id} className="w-full flex items-center gap-2 p-4 hover:bg-[#F8F9FA] transition-colors">
                  <button data-testid={`admin-client-${c.id}`} onClick={() => openClient(c)} className="flex-1 flex items-center justify-between text-left min-w-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <UserCircle size={20} className="text-gold shrink-0" />
                      <div className="min-w-0">
                        <p className="text-navy font-medium truncate flex items-center gap-2">{c.name}
                          {!c.has_account && <span className="text-[9px] uppercase tracking-widest bg-amber-50 text-amber-700 px-1.5 py-0.5" title="Client issu d'une réservation, sans compte enregistré">sans compte</span>}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}{c.country ? ` · ${c.country === "Autre" ? "Autre pays" : c.country}` : ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <span className="text-xs text-muted-foreground hidden sm:inline">{c.bookings_count} dossier{c.bookings_count > 1 ? "s" : ""} · {c.documents_count} doc{c.documents_count > 1 ? "s" : ""}</span>
                      <ChevronRight size={16} className="text-muted-foreground" />
                    </div>
                  </button>
                  {isAdmin && c.has_account && <TrashBtn testid={`trash-client-${c.id}`} onClick={() => deleteClient(c)} />}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="contrats" className="mt-5" data-testid="contrats-content">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <h3 className="font-heading text-lg text-navy mb-2 flex items-center gap-2"><FileSignature size={18} className="text-gold" /> Contrats ({contracts.length})</h3>
                <div className="bg-white border border-border divide-y divide-border">
                  {contracts.length === 0 && <p className="p-6 text-muted-foreground text-sm">Aucun contrat.</p>}
                  {contracts.map((ct) => (
                    <div key={ct.id} data-testid={`admin-contract-${ct.id}`} className="flex items-center justify-between p-4">
                      <div className="min-w-0">
                        <p className="text-navy font-medium truncate">{ct.pack_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{ct.user_name} · {ct.user_email}</p>
                        <span className={`inline-block mt-1 text-[10px] uppercase tracking-widest px-2 py-0.5 ${ct.signed ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>{ct.signed ? "Signé" : "En attente de signature"}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button data-testid={`download-contract-${ct.id}`} onClick={() => downloadPdf("contracts", ct.id)} className="flex items-center gap-1.5 text-sm text-navy hover:text-gold transition-colors"><Download size={15} /> PDF</button>
                        {isAdmin && <TrashBtn testid={`trash-contract-${ct.id}`} onClick={() => softDelete("contract", ct.id)} />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-heading text-lg text-navy mb-2 flex items-center gap-2"><FileText size={18} className="text-gold" /> Factures ({invoices.length})</h3>
                <div className="bg-white border border-border divide-y divide-border">
                  {invoices.length === 0 && <p className="p-6 text-muted-foreground text-sm">Aucune facture.</p>}
                  {invoices.map((inv) => (
                    <div key={inv.id} data-testid={`admin-invoice-${inv.id}`} className="flex items-center justify-between p-4">
                      <div className="min-w-0">
                        <p className="text-navy font-medium truncate">{inv.number}</p>
                        <p className="text-xs text-muted-foreground truncate">{inv.user_name} · {CURRENCY(inv.amount)}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button data-testid={`download-invoice-${inv.id}`} onClick={() => downloadPdf("invoices", inv.id)} className="flex items-center gap-1.5 text-sm text-navy hover:text-gold transition-colors"><Download size={15} /> PDF</button>
                        {isAdmin && <TrashBtn testid={`trash-invoice-${inv.id}`} onClick={() => softDelete("invoice", inv.id)} />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-5">
            <div className="bg-white border border-border divide-y divide-border">
              {contacts.length === 0 && <p className="p-6 text-muted-foreground text-sm">Aucun message.</p>}
              {contacts.map((c) => (
                <div key={c.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-navy font-medium">{c.subject}</p>
                    <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{c.name} · {c.email} {c.phone && `· ${c.phone}`}</p>
                  <p className="text-sm text-slate-700 mt-2">{c.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="conseillers" className="mt-5" data-testid="conseillers-content">
            <div className="bg-white border border-border p-5 mb-4">
              <p className="text-sm text-muted-foreground mb-4">Gérez les conseillers affichés sur la page de rendez-vous (nom, rôle, présentation). Les modifications sont visibles immédiatement côté public.</p>
              <form onSubmit={submitAdvisor} data-testid="advisor-form" className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs text-muted-foreground">Nom</label>
                  <input data-testid="advisor-name" value={advForm.name} onChange={(e) => setAdvForm({ ...advForm, name: e.target.value })} required className="w-full mt-1 border border-border px-3 py-2 text-sm" placeholder="Ex. Amanda" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Rôle</label>
                  <input data-testid="advisor-role" value={advForm.role} onChange={(e) => setAdvForm({ ...advForm, role: e.target.value })} required className="w-full mt-1 border border-border px-3 py-2 text-sm" placeholder="Ex. Conseillère immigration" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-muted-foreground">Présentation</label>
                  <textarea data-testid="advisor-description" value={advForm.description} onChange={(e) => setAdvForm({ ...advForm, description: e.target.value })} rows={3} className="w-full mt-1 border border-border px-3 py-2 text-sm" placeholder="Courte présentation rassurante…" />
                </div>
                <div className="sm:col-span-2 flex items-center gap-3">
                  <button data-testid="advisor-submit-btn" type="submit" className="bg-gold hover:bg-gold-hover text-white px-4 py-2 text-sm transition-colors">{advEditId ? "Enregistrer les modifications" : "Ajouter le conseiller"}</button>
                  {advEditId && <button type="button" data-testid="advisor-cancel-btn" onClick={cancelAdvisorEdit} className="border border-border text-navy px-4 py-2 text-sm hover:bg-[#F8F9FA] transition-colors">Annuler</button>}
                </div>
              </form>
            </div>
            <div className="bg-white border border-border divide-y divide-border" data-testid="advisor-list">
              {advisors.length === 0 && <p className="p-6 text-muted-foreground text-sm">Aucun conseiller. Ajoutez-en un ci-dessus.</p>}
              {advisors.map((a) => (
                <div key={a.id} data-testid={`advisor-row-${a.id}`} className="flex items-start justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="text-navy font-medium">{a.name} <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 ml-1 bg-gold/10 text-gold">{a.role}</span></p>
                    {a.description && <p className="text-xs text-muted-foreground mt-1">{a.description}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`advisor-edit-${a.id}`} onClick={() => editAdvisor(a)} className="text-xs border border-border text-navy px-3 py-1.5 hover:bg-[#F8F9FA] transition-colors">Modifier</button>
                    <TrashBtn testid={`advisor-delete-${a.id}`} onClick={() => deleteAdvisor(a)} />
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>


          <TabsContent value="avis" className="mt-5" data-testid="avis-content">
            {/* CONFIGURATION DE LA SECTION AVIS GOOGLE */}
            <div className="bg-white border border-border p-5 mb-4" data-testid="google-config-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="font-heading text-xl text-navy">Section « Avis Google »</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Renseignez la note moyenne et le nombre total d'avis affichés sur votre fiche Google, ainsi que le lien de votre fiche.
                    La section apparaît sur l'accueil, la page Nos packs et la page Avis. Laissez « Affichage activé » décoché pour la masquer.
                  </p>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${gCfg.api_configured ? "bg-gold/10 text-gold" : "bg-navy/10 text-navy"}`}>
                  {gCfg.api_configured ? "Synchronisation Google active" : "Mode manuel"}
                </span>
              </div>
              <form onSubmit={saveGoogleConfig} className="grid gap-3 sm:grid-cols-2 mt-4" data-testid="google-config-form">
                <div>
                  <label className="text-xs text-muted-foreground">Note moyenne Google (0 à 5)</label>
                  <input data-testid="google-cfg-rating" type="number" step="0.1" min="0" max="5" value={gCfg.rating}
                    onChange={(e) => setGCfg({ ...gCfg, rating: e.target.value })}
                    className="w-full mt-1 border border-border px-3 py-2 text-sm" placeholder="Ex. 4.9" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Nombre total d'avis Google</label>
                  <input data-testid="google-cfg-count" type="number" min="0" value={gCfg.review_count}
                    onChange={(e) => setGCfg({ ...gCfg, review_count: e.target.value })}
                    className="w-full mt-1 border border-border px-3 py-2 text-sm" placeholder="Ex. 27" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-muted-foreground">Lien de votre fiche Google (bouton « Voir tous nos avis »)</label>
                  <input data-testid="google-cfg-profile" value={gCfg.profile_url}
                    onChange={(e) => setGCfg({ ...gCfg, profile_url: e.target.value })}
                    className="w-full mt-1 border border-border px-3 py-2 text-sm" placeholder="https://g.page/…" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-muted-foreground">Lien « Laisser un avis » (facultatif)</label>
                  <input data-testid="google-cfg-write" value={gCfg.write_review_url}
                    onChange={(e) => setGCfg({ ...gCfg, write_review_url: e.target.value })}
                    className="w-full mt-1 border border-border px-3 py-2 text-sm" placeholder="https://g.page/…/review" />
                </div>
                <div className="sm:col-span-2 flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-navy">
                    <input data-testid="google-cfg-enabled" type="checkbox" checked={!!gCfg.enabled}
                      onChange={(e) => setGCfg({ ...gCfg, enabled: e.target.checked })} />
                    Affichage activé sur le site
                  </label>
                  <button data-testid="google-cfg-save-btn" type="submit" className="bg-gold hover:bg-gold-hover text-white px-4 py-2 text-sm transition-colors">Enregistrer</button>
                </div>
              </form>
            </div>

            <div className="bg-white border border-border p-5 mb-4">
              <p className="text-sm text-muted-foreground mb-4">Ajoutez vos avis Google (copier/coller) et gérez tous les avis. Les avis laissés par les visiteurs apparaissent automatiquement ici (vous pouvez les masquer ou les supprimer).</p>
              <form onSubmit={addReview} data-testid="admin-review-form" className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs text-muted-foreground">Nom</label>
                  <input data-testid="admin-review-name" value={revForm.name} onChange={(e) => setRevForm({ ...revForm, name: e.target.value })} required className="w-full mt-1 border border-border px-3 py-2 text-sm" placeholder="Ex. Marie D." />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Note</label>
                  <select data-testid="admin-review-rating" value={revForm.rating} onChange={(e) => setRevForm({ ...revForm, rating: Number(e.target.value) })} className="w-full mt-1 border border-border px-3 py-2 text-sm bg-white">
                    {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{`${n} étoile${n > 1 ? "s" : ""}`}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-muted-foreground">Avis</label>
                  <textarea data-testid="admin-review-text" value={revForm.text} onChange={(e) => setRevForm({ ...revForm, text: e.target.value })} rows={3} required className="w-full mt-1 border border-border px-3 py-2 text-sm" placeholder="Texte de l'avis…" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-muted-foreground">Date affichée (facultatif — ex. « il y a 2 semaines »)</label>
                  <input data-testid="admin-review-date" value={revForm.date_label} onChange={(e) => setRevForm({ ...revForm, date_label: e.target.value })} className="w-full mt-1 border border-border px-3 py-2 text-sm" placeholder="il y a 2 semaines" />
                </div>
                <div className="sm:col-span-2 flex items-center gap-3">
                  <select data-testid="admin-review-source" value={revForm.source} onChange={(e) => setRevForm({ ...revForm, source: e.target.value })} className="border border-border px-3 py-2 text-sm bg-white">
                    <option value="google">Avis Google</option>
                    <option value="platform">Avis plateforme</option>
                  </select>
                  <button data-testid="admin-review-add-btn" type="submit" className="bg-gold hover:bg-gold-hover text-white px-4 py-2 text-sm transition-colors">Ajouter l'avis</button>
                </div>
              </form>
            </div>
            <div className="bg-white border border-border divide-y divide-border" data-testid="admin-review-list">
              {reviews.length === 0 && <p className="p-6 text-muted-foreground text-sm">Aucun avis pour le moment.</p>}
              {reviews.map((r) => (
                <div key={r.id} data-testid={`admin-review-row-${r.id}`} className={`flex items-start justify-between gap-3 p-4 ${r.visible === false ? "opacity-50" : ""}`}>
                  <div className="min-w-0">
                    <p className="text-navy font-medium">{r.name} <span className="text-gold text-sm">{"★".repeat(r.rating)}</span> <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ml-1 ${r.source === "google" ? "bg-gold/10 text-gold" : "bg-navy/10 text-navy"}`}>{r.source === "google" ? "Google" : "Plateforme"}</span></p>
                    <p className="text-xs text-muted-foreground mt-1">{r.text}</p>
                    {r.date_label && <p className="text-[11px] text-muted-foreground/80 mt-1 italic">Date affichée : {r.date_label}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`review-toggle-${r.id}`} onClick={() => toggleReview(r)} className="text-xs border border-border text-navy px-3 py-1.5 hover:bg-[#F8F9FA] transition-colors">{r.visible === false ? "Afficher" : "Masquer"}</button>
                    <TrashBtn testid={`review-delete-${r.id}`} onClick={() => deleteReview(r)} />
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="threads" className="mt-5">
            <div className="bg-white border border-border divide-y divide-border">
              {threads.length === 0 && <p className="p-6 text-muted-foreground text-sm">Aucune conversation.</p>}
              {threads.map((t) => (
                <button key={t.user_id} data-testid={`admin-thread-${t.user_id}`} onClick={() => openThread(t)} className="w-full flex items-center justify-between p-4 hover:bg-[#F8F9FA] transition-colors text-left">
                  <div className="min-w-0">
                    <p className="text-navy font-medium truncate">{t.user_name} <span className="text-xs text-muted-foreground">· {t.user_email}</span></p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{t.last}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {t.unread > 0 && <span className="bg-gold text-white text-xs px-2 py-0.5 rounded-full">{t.unread}</span>}
                    <MessageSquare size={16} className="text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="rendezvous" className="mt-5">
            <div className="flex justify-end mb-4">
              <Select value={apptFilter} onValueChange={setApptFilter}>
                <SelectTrigger data-testid="appt-filter" className="w-full sm:w-52 rounded-none bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="confirmed">Confirmés</SelectItem>
                  <SelectItem value="done">Terminés</SelectItem>
                  <SelectItem value="cancelled">Annulés</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-white border border-border divide-y divide-border">
              {fAppts.length === 0 && <p className="p-6 text-muted-foreground text-sm">Aucun rendez-vous.</p>}
              {fAppts.map((a) => (
                <div key={a.id} data-testid={`admin-appt-${a.id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 bg-navy text-gold flex items-center justify-center">{a.mode === "visio" ? <Video size={16} /> : <Phone size={16} />}</div>
                    <div>
                      <p className="text-navy font-medium">{a.user_name} <span className="text-xs text-muted-foreground">· {a.mode}</span></p>
                      <p className="text-xs text-muted-foreground">Souhaité : {new Date(a.preferred_date).toLocaleString("fr-FR")}{a.note ? ` · ${a.note}` : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-1 ${a.status === "confirmed" ? "bg-green-50 text-green-700" : a.status === "done" ? "bg-blue-50 text-blue-700" : a.status === "cancelled" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{a.status === "confirmed" ? "Confirmé" : a.status === "done" ? "Terminé" : a.status === "cancelled" ? "Annulé" : "En attente"}</span>
                    {a.status !== "confirmed" && a.status !== "done" && (
                      <button data-testid={`confirm-appt-${a.id}`} onClick={() => setApptEdit({ ...a })} className="text-sm bg-gold hover:bg-gold-hover text-white px-3 py-1.5 transition-colors">Confirmer</button>
                    )}
                    {a.status === "confirmed" && (
                      <button data-testid={`done-appt-${a.id}`} onClick={() => setApptStatus(a, "done")} className="text-sm bg-navy hover:bg-navy-light text-white px-3 py-1.5 transition-colors">Marquer terminé</button>
                    )}
                    {a.status !== "cancelled" && a.status !== "done" && (
                      <button data-testid={`cancel-appt-${a.id}`} onClick={() => setApptStatus(a, "cancelled")} className="text-sm border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 transition-colors">Annuler</button>
                    )}
                    {isAdmin && <TrashBtn testid={`trash-appointment-${a.id}`} onClick={() => softDelete("appointment", a.id)} />}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {isAdmin && (
          <TabsContent value="paiements" className="mt-5" data-testid="paiements-content">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="bg-white border border-border p-5">
                <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground">Revenu total</span><Euro size={18} className="text-gold" /></div>
                <div className="font-heading text-2xl text-navy mt-2">{payments ? CURRENCY(payments.total_revenue) : "…"}</div>
              </div>
              <div className="bg-white border border-border p-5">
                <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground">Paiements réussis</span><CheckCircle2 size={18} className="text-gold" /></div>
                <div className="font-heading text-2xl text-navy mt-2">{payments ? payments.paid_count : "…"}</div>
              </div>
              <div className="bg-white border border-border p-5">
                <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground">Transactions</span><CreditCard size={18} className="text-gold" /></div>
                <div className="font-heading text-2xl text-navy mt-2">{payments ? payments.total_count : "…"}</div>
              </div>
            </div>
            <div className="bg-white border border-border divide-y divide-border">
              {(!payments || payments.transactions.length === 0) && <p className="p-6 text-muted-foreground text-sm">Aucune transaction.</p>}
              {payments?.transactions.map((tx) => (
                <div key={tx.id} data-testid={`admin-payment-${tx.id}`} className="flex items-center justify-between p-4">
                  <div className="min-w-0">
                    <p className="text-navy font-medium truncate">{tx.user_email}</p>
                    <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleString("fr-FR")}</p>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                    <span className={`text-xs px-2 py-1 ${tx.payment_status === "paid" ? "bg-green-50 text-green-700" : tx.payment_status === "initiated" ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-700"}`}>{tx.payment_status === "paid" ? "Payé" : tx.payment_status === "initiated" ? "Initié" : tx.payment_status}</span>
                    <span className="text-sm text-navy font-medium">{CURRENCY(tx.amount)}</span>
                    {isAdmin && <TrashBtn testid={`trash-payment-${tx.id}`} onClick={() => softDelete("payment", tx.id)} />}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          )}

          {isAdmin && (
          <TabsContent value="journal" className="mt-5" data-testid="journal-content">
            <div className="bg-white border border-border divide-y divide-border">
              {activity.length === 0 && <p className="p-6 text-muted-foreground text-sm">Aucune activité enregistrée.</p>}
              {activity.map((a) => (
                <div key={a.id} data-testid={`admin-activity-${a.id}`} className="flex items-start gap-3 p-4">
                  <div className="h-9 w-9 bg-navy text-gold flex items-center justify-center shrink-0"><History size={16} /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-navy">{a.summary}{a.target_user_name ? <span className="text-muted-foreground"> — {a.target_user_name}</span> : null}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {a.actor_name} <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ml-1 ${a.actor_role === "admin" ? "bg-navy text-white" : "bg-gold/10 text-gold"}`}>{a.actor_role}</span>
                      <span className="ml-2">{new Date(a.created_at).toLocaleString("fr-FR")}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          )}

          {isAdmin && (
          <TabsContent value="staff" className="mt-5">
            <form onSubmit={createStaff} className="bg-white border border-border p-5 flex flex-col sm:flex-row gap-3 sm:items-end flex-wrap" data-testid="staff-create-form">
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs text-muted-foreground">Nom</label>
                <input data-testid="staff-name" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} required className="w-full mt-1 border border-border px-3 py-2 text-sm" />
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className="text-xs text-muted-foreground">Email</label>
                <input data-testid="staff-email" type="email" value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} required className="w-full mt-1 border border-border px-3 py-2 text-sm" />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs text-muted-foreground">Mot de passe</label>
                <input data-testid="staff-password" type="text" value={newStaff.password} onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })} required minLength={6} className="w-full mt-1 border border-border px-3 py-2 text-sm" />
              </div>
              <button data-testid="staff-create-btn" type="submit" className="bg-gold hover:bg-gold-hover text-white px-4 py-2 text-sm transition-colors">Créer un employé</button>
            </form>
            <div className="bg-white border border-border divide-y divide-border mt-4">
              {staffList.map((s) => (
                <div key={s.id} data-testid={`staff-row-${s.id}`} className="flex items-center justify-between p-4">
                  <div>
                    <p className="text-navy font-medium">{s.name} <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ml-2 ${s.role === "admin" ? "bg-navy text-white" : "bg-gold/10 text-gold"}`}>{s.role}</span></p>
                    <p className="text-xs text-muted-foreground">{s.email}</p>
                  </div>
                  {s.role === "staff" && <TrashBtn testid={`staff-delete-${s.id}`} onClick={() => softDelete("staff", s.id)} />}
                </div>
              ))}
            </div>
          </TabsContent>
          )}

          {isAdmin && (
          <TabsContent value="sydney" className="mt-5" data-testid="sydney-content">
            <SydneyIntegrationPanel />
          </TabsContent>
          )}

          <TabsContent value="preinscriptions" className="mt-5" data-testid="preinscriptions-content">
            <div className="bg-white border border-border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center"><BellRing size={18} /></div>
                <div>
                  <p className="text-navy font-medium">Préinscriptions Canada 2027</p>
                  <p className="text-xs text-muted-foreground">{preregs.length} préinscription{preregs.length > 1 ? "s" : ""} · collectées via la page « Préinscription Canada 2027 »</p>
                </div>
              </div>
              <button
                data-testid="prereg-export-btn"
                onClick={exportPreregCsv}
                disabled={preregs.length === 0}
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-white px-4 py-2 text-sm transition-colors disabled:opacity-50"
              >
                <Download size={16} /> Exporter en CSV
              </button>
            </div>
            <div className="bg-white border border-border divide-y divide-border mt-4" data-testid="prereg-list">
              {preregs.length === 0 && <p className="p-6 text-muted-foreground text-sm">Aucune préinscription pour le moment.</p>}
              {preregs.map((p) => (
                <div key={p.id} data-testid={`prereg-row-${p.id}`} className="flex items-start justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="text-navy font-medium">
                      {p.name} <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 ml-1 bg-gold/10 text-gold">{p.destination}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3">
                      <span className="inline-flex items-center gap-1"><Mail size={12} className="text-gold" /> {p.email}</span>
                      {p.phone && <span>{p.phone}</span>}
                      <span>{new Date(p.created_at).toLocaleString("fr-FR")}</span>
                    </p>
                    {p.message && <p className="text-xs text-muted-foreground mt-1 italic">« {p.message} »</p>}
                  </div>
                  <TrashBtn testid={`prereg-delete-${p.id}`} onClick={() => deletePrereg(p)} />
                </div>
              ))}
            </div>
          </TabsContent>

          {isAdmin && (
          <TabsContent value="waitlist" className="mt-5" data-testid="waitlist-content">            <div className="bg-white border border-border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center"><BellRing size={18} /></div>
                <div>
                  <p className="text-navy font-medium">Liste d'attente ouverture</p>
                  <p className="text-xs text-muted-foreground">{launchList.length} inscription{launchList.length > 1 ? "s" : ""} · e-mails collectés via la section « Annonce officielle »</p>
                </div>
              </div>
              <button
                data-testid="waitlist-export-btn"
                onClick={exportLaunchCsv}
                disabled={launchList.length === 0}
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-white px-4 py-2 text-sm transition-colors disabled:opacity-50"
              >
                <Download size={16} /> Exporter en CSV
              </button>
            </div>
            <div className="bg-white border border-border divide-y divide-border mt-4">
              {launchList.length === 0 && <p className="p-6 text-muted-foreground text-sm">Aucune inscription pour le moment.</p>}
              {launchList.map((l) => (
                <div key={l.email} data-testid={`waitlist-row-${l.email}`} className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail size={15} className="text-gold shrink-0" />
                    <span className="text-navy text-sm truncate">{l.email}</span>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0 ml-3">{new Date(l.created_at).toLocaleString("fr-FR")}</span>
                </div>
              ))}
            </div>
          </TabsContent>
          )}
          {isAdmin && (
          <TabsContent value="corbeille" className="mt-5" data-testid="corbeille-content">
            <div className="bg-white border border-border p-5 flex items-center gap-3 mb-4">
              <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center shrink-0"><Trash2 size={18} /></div>
              <div>
                <p className="text-navy font-medium">Corbeille de l'administration</p>
                <p className="text-xs text-muted-foreground">{trash.length} élément{trash.length > 1 ? "s" : ""} supprimé{trash.length > 1 ? "s" : ""}. Restaurez ou supprimez définitivement. Accès réservé aux administrateurs.</p>
              </div>
            </div>
            <div className="bg-white border border-border divide-y divide-border">
              {trash.length === 0 && <p className="p-6 text-muted-foreground text-sm">La corbeille est vide.</p>}
              {trash.map((it) => (
                <div key={it.id} data-testid={`trash-item-${it.id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="text-navy font-medium truncate flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-widest bg-navy/5 text-navy px-2 py-0.5 shrink-0">{TRASH_TYPE_FR[it.entity_type] || it.entity_type}</span>
                      <span className="truncate">{it.label}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Supprimé le {new Date(it.deleted_at).toLocaleString("fr-FR")}{it.deleted_by_email ? ` par ${it.deleted_by_email}` : ""}
                      {it.related_count > 0 ? ` · ${it.related_count} donnée(s) liée(s)` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`trash-restore-${it.id}`} onClick={() => restoreTrash(it.id)} className="inline-flex items-center gap-1.5 text-sm border border-green-300 text-green-700 hover:bg-green-50 px-3 py-1.5 transition-colors"><RefreshCw size={14} /> Restaurer</button>
                    <button data-testid={`trash-purge-${it.id}`} onClick={() => purgeTrash(it.id)} className="inline-flex items-center gap-1.5 text-sm border border-red-300 text-red-600 hover:bg-red-600 hover:text-white transition-colors px-3 py-1.5"><Trash2 size={14} /> Supprimer définitivement</button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          )}
        </Tabs>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg rounded-none max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-2xl text-navy">{selected.pack_name}</DialogTitle></DialogHeader>
              <div className="space-y-5">
                <div className="text-sm text-muted-foreground">{selected.user_name} · {selected.user_email}</div>
                <div>
                  <label className="text-sm text-navy font-medium">Étape du dossier</label>
                  <Select disabled={selected.payment_status !== "paid"} value={String(selected.step_index)} onValueChange={(v) => updateStep(selected, v)}>
                    <SelectTrigger data-testid="admin-step-select" className="mt-2 rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {selected.steps?.map((s, i) => <SelectItem key={s} value={String(i)}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {selected.payment_status !== "paid" && <p className="text-xs text-amber-600 mt-1">Le paiement n'est pas encore confirmé.</p>}
                </div>
                <div>
                  <label className="text-sm text-navy font-medium">Note pour le client</label>
                  <Textarea data-testid="admin-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-2 rounded-none" placeholder="Message visible par le client…" />
                  <button data-testid="admin-save-note" onClick={saveNote} className="mt-2 bg-navy text-white px-4 py-2 text-sm hover:bg-navy-light transition-colors">Enregistrer la note</button>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-navy font-medium">Documents du client ({docs.length})</label>
                    <label data-testid="admin-upload-doc-label" className={`flex items-center gap-2 bg-gold hover:bg-gold-hover text-white px-3 py-1.5 text-sm cursor-pointer transition-colors ${uploadingDoc ? "opacity-60 pointer-events-none" : ""}`}>
                      {uploadingDoc ? <><Loader2 size={14} className="animate-spin" /> Envoi…</> : <><Upload size={14} /> Envoyer un document</>}
                      <input type="file" data-testid="admin-doc-file-input" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={uploadClientDoc} />
                    </label>
                  </div>
                  <div className="mt-2 space-y-2">
                    {docs.length === 0 && <p className="text-xs text-muted-foreground">Aucun document.</p>}
                    {docs.map((d) => (
                      <button key={d.id} onClick={() => download(d.id)} className="w-full flex items-center gap-3 p-2 border border-border hover:border-gold transition-colors text-left">
                        <FileText size={16} className={d.uploaded_by === "advisor" ? "text-gold" : "text-navy"} />
                        <span className="text-sm text-navy truncate flex-1">{d.original_filename}</span>
                        {d.uploaded_by === "advisor" && <span className="text-[10px] uppercase tracking-widest bg-gold/10 text-gold px-1.5 py-0.5">Agence</span>}
                        <span className="text-xs text-muted-foreground">{d.doc_type}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Client detail dialog */}
      <Dialog open={!!clientDetail} onOpenChange={(o) => !o && setClientDetail(null)}>
        <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="client-detail-dialog">
          {clientDetail?.loading && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-2xl text-navy">Fiche client</DialogTitle>
                <DialogDescription>Chargement des informations…</DialogDescription>
              </DialogHeader>
              <p className="text-sm text-muted-foreground py-6">Chargement…</p>
            </>
          )}
          {clientDetail && !clientDetail.loading && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-navy">{clientDetail.client.name}</DialogTitle>
                <DialogDescription>{clientDetail.client.email}{clientDetail.client.phone ? ` · ${clientDetail.client.phone}` : ""}{clientDetail.client.country ? ` · ${clientDetail.client.country === "Autre" ? "Autre pays" : clientDetail.client.country}` : ""}</DialogDescription>
              </DialogHeader>
              <div className="space-y-5">
                <div>
                  <p className="text-sm font-medium text-navy mb-2">Dossiers ({clientDetail.bookings.length})</p>
                  <div className="border border-border divide-y divide-border">
                    {clientDetail.bookings.length === 0 && <p className="p-3 text-xs text-muted-foreground">Aucun dossier.</p>}
                    {clientDetail.bookings.map((b) => (
                      <div key={b.id} className="flex items-center justify-between p-3">
                        <span className="text-sm text-navy">{b.pack_name}</span>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs px-2 py-0.5 ${b.payment_status === "paid" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>{b.payment_status === "paid" ? b.steps?.[b.step_index] : "Non payé"}</span>
                          <span className="text-sm text-navy font-medium">{CURRENCY(b.amount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-navy mb-2">Documents ({clientDetail.documents.length})</p>
                  <div className="border border-border divide-y divide-border">
                    {clientDetail.documents.length === 0 && <p className="p-3 text-xs text-muted-foreground">Aucun document.</p>}
                    {clientDetail.documents.map((d) => (
                      <button key={d.id} onClick={() => download(d.id)} className="w-full flex items-center gap-3 p-3 hover:bg-[#F8F9FA] transition-colors text-left">
                        <FileText size={15} className={d.uploaded_by === "advisor" ? "text-gold" : "text-navy"} />
                        <span className="text-sm text-navy truncate flex-1">{d.original_filename}</span>
                        {d.uploaded_by === "advisor" && <span className="text-[10px] uppercase tracking-widest bg-gold/10 text-gold px-1.5 py-0.5">Agence</span>}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-navy mb-2">Rendez-vous ({clientDetail.appointments.length})</p>
                  <div className="border border-border divide-y divide-border">
                    {clientDetail.appointments.length === 0 && <p className="p-3 text-xs text-muted-foreground">Aucun rendez-vous.</p>}
                    {clientDetail.appointments.map((a) => (
                      <div key={a.id} className="flex items-center justify-between p-3">
                        <span className="text-sm text-navy">{a.mode} · {new Date(a.preferred_date).toLocaleString("fr-FR")}</span>
                        <span className={`text-xs px-2 py-0.5 ${a.status === "confirmed" ? "bg-green-50 text-green-700" : a.status === "done" ? "bg-blue-50 text-blue-700" : a.status === "cancelled" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{a.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {isAdmin && (
                  <div className="pt-3 border-t border-border">
                    <button
                      data-testid="delete-client-btn"
                      onClick={() => deleteClient(clientDetail.client)}
                      className="flex items-center gap-2 text-sm text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 px-4 py-2.5 transition-colors"
                    >
                      <Trash2 size={16} /> Supprimer ce compte client
                    </button>
                    <p className="text-xs text-muted-foreground mt-2">Le compte et ses données (dossiers, documents, rendez-vous, messages) sont déplacés dans la Corbeille. Vous pourrez les restaurer ou les supprimer définitivement depuis l'onglet « Corbeille ».</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>


      {/* Thread messaging dialog */}
      <Dialog open={!!activeThread} onOpenChange={(o) => !o && setActiveThread(null)}>
        <DialogContent className="max-w-lg rounded-none">
          {activeThread && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-2xl text-navy">{activeThread.user_name}</DialogTitle></DialogHeader>
              <div className="flex flex-col h-[55vh]">
                <div className="flex-1 overflow-y-auto space-y-3 p-1" data-testid="admin-thread-messages">
                  {threadMsgs.length === 0 && <p className="text-center text-sm text-muted-foreground mt-8">Aucun message.</p>}
                  {threadMsgs.map((m) => {
                    const mine = m.sender === "advisor";
                    return (
                      <div key={m.id} className={`flex items-center gap-1 group ${mine ? "justify-end" : "justify-start"}`}>
                        {isAdmin && mine && <TrashBtn testid={`trash-message-${m.id}`} onClick={() => softDelete("message", m.id)} className="opacity-0 group-hover:opacity-100" />}
                        <div className={`max-w-[80%] px-3 py-2 text-sm ${mine ? "bg-gold text-white" : "bg-[#F3F4F6] text-navy border border-border"}`}>
                          <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
                          <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-muted-foreground"}`}>{new Date(m.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
                        </div>
                        {isAdmin && !mine && <TrashBtn testid={`trash-message-${m.id}`} onClick={() => softDelete("message", m.id)} className="opacity-0 group-hover:opacity-100" />}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2 pt-3 border-t border-border">
                  <input data-testid="admin-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()}
                    placeholder="Répondre…" className="flex-1 border border-border px-3 py-2 text-sm focus:outline-none focus:border-gold" />
                  <button data-testid="admin-reply-send" onClick={sendReply} className="bg-navy text-white px-4 flex items-center transition-colors hover:bg-navy-light"><Send size={16} /></button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Appointment confirm dialog */}
      <Dialog open={!!apptEdit} onOpenChange={(o) => !o && setApptEdit(null)}>
        <DialogContent className="rounded-none">
          {apptEdit && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-2xl text-navy">Confirmer le rendez-vous</DialogTitle>
                <DialogDescription>Définissez la date confirmée et un lien de réunion, puis notifiez le client.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{apptEdit.user_name} · {apptEdit.mode} · souhaité le {new Date(apptEdit.preferred_date).toLocaleString("fr-FR")}</p>
                <div>
                  <label className="text-sm text-navy font-medium">Date &amp; heure confirmées</label>
                  <input type="datetime-local" data-testid="appt-confirm-date" value={apptEdit.confirmed_datetime || ""} onChange={(e) => setApptEdit({ ...apptEdit, confirmed_datetime: e.target.value })} className="mt-2 w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-gold" />
                </div>
                <div>
                  <label className="text-sm text-navy font-medium">Lien de réunion (visio) — optionnel</label>
                  <input data-testid="appt-confirm-link" value={apptEdit.meeting_link || ""} onChange={(e) => setApptEdit({ ...apptEdit, meeting_link: e.target.value })} placeholder="https://meet.google.com/…" className="mt-2 w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-gold" />
                </div>
                <button data-testid="appt-confirm-submit" onClick={confirmAppt} className="w-full bg-gold hover:bg-gold-hover text-white py-3 font-medium transition-colors flex items-center justify-center gap-2"><CheckCircle2 size={16} /> Confirmer &amp; notifier le client</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
